// Web → Analytika (owner / manager / marketing).
//
// Vlastní měření návštěvnosti fitway40.cz. Data z /api/web-analytics, které
// čte tabulku web_events plněnou skriptem fw40a.js z webu. Žádné cookies,
// žádná Google Analytics, nic se nesdílí ven.
//
// Nejdůležitější graf je „Kam až došly" — ukazuje, kolik procent žen vidělo
// kterou sekci prodejní stránky. Kde křivka spadne, tam text přestává držet.
import React, { useEffect, useMemo, useState } from "react";
import { BarChart3, Users, Eye, ShoppingCart, Percent, Timer, MousePointerClick, AlertTriangle, TrendingDown, TrendingUp, Globe, Trash2, ArrowUp, ArrowDown, ArrowRight, Minus, Wallet, MailCheck, Filter, CheckCircle2, Sparkles, Clock, RefreshCw, Download, Receipt } from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { useApiData } from "../../lib/api.js";
import GradientHeader from "../../components/GradientHeader.jsx";
import { Stranka, Zalozky, Hlaska, ErrorNote, SkeletonCards, EmptyState, NabidkaAkci } from "../../components/ui.jsx";
import Card from "../../components/Card.jsx";
import KpiCard from "../../components/KpiCard.jsx";
import InfoTip from "../../components/InfoTip.jsx";
import ConfirmDialog from "../../components/ConfirmDialog.jsx";
import FiltrObdobi, { rozsahZFiltru, popisObdobi, popisSrovnani } from "../../components/FiltrObdobi.jsx";
import { hasRole } from "../../lib/auth.js";
import { apiFetch } from "../../lib/api.js";
import FlowMapa from "./FlowMapa.jsx";
import Zive from "./Zive.jsx";
import ExportStatistik from "./ExportStatistik.jsx";
import TrzbyFapi from "./TrzbyFapi.jsx";
import DetailProdejky from "./DetailProdejky.jsx";
import DetailKviz from "./DetailKviz.jsx";

const BRAND = "#6E519F";
const GRID = "#EAE6EF";
const ZELENA = "#10b981";
const JANTAR = "#f59e0b";
const RUZOVA = "#ec4899";

const pct = (a, b) => (b > 0 ? Math.round((a / b) * 1000) / 10 : 0);
const cas = (s) => {
  const n = Number(s) || 0;
  if (n < 60) return `${n} s`;
  return `${Math.floor(n / 60)} m ${String(n % 60).padStart(2, "0")} s`;
};

export default function WebAnalytika() {
  const [tab, setTab] = useState("prehled");
  const [filtr, setFiltr] = useState(() => {
    const ted = new Date();
    return {
      rezim: "dny",
      dny: 30,
      mesic: ted.getMonth(),
      ctvrtleti: Math.floor(ted.getMonth() / 3),
      rok: ted.getFullYear(),
      srovnat: false,
    };
  });

  // Která varianta prodejní cesty. Srovnávací tabulka níž ukazuje obě vždy,
  // tenhle přepínač řídí zbytek čísel na stránce.
  const [varianta, setVarianta] = useState("all");

  // Vynulování statistik, jen pro ownera.
  const jeOwner = hasRole("owner");
  const [mazeDialog, setMazeDialog] = useState(false);
  // 14. 9. 2026 (fáze 6): export a Tržby z FAPI se otevírají z hlavičky, okna jsou řízená odsud.
  const [exportOpen, setExportOpen] = useState(false);
  const [fapiOpen, setFapiOpen] = useState(false);
  const [maze, setMaze] = useState(false);
  const [hlaska, setHlaska] = useState(null);

  // Varianta prodejni cesty ma smysl jen tam, kde se cesta na varianty deli:
  // v Prehledu a na Prodejni strance. V kvizu a v mape toku je zena jeste pred
  // rozcestim, takze filtr tam jen mazal polovinu cisel (18.8.2026).
  // Varianta prodejní cesty se filtruje JEN na záložce Prodejní stránka.
  //
  // V Přehledu nedává smysl: jsou tam čísla za celý web (návštěvnice, kvíz,
  // potvrzení e-mailu, zdroje, reklama) a ta žádnou variantu nemají, takže
  // filtr je vynuloval. Kvíz při „Bez slevy" ukazoval nulu, i když ho vyplnily
  // stovky žen (18.8.2026). Rozpad na varianty je v Přehledu v tabulce
  // „Varianty prodejní cesty", která ukazuje obě vedle sebe.
  //
  // Na Prodejní stránce je naopak volba povinná: obě adresy (/jsz/ a
  // /jsz-sleva/) mají stejné názvy sekcí a smíchané dohromady by trychtýř
  // nic neřekl. Proto tam „Vše" není a drží se „Bez slevy".
  const variantaPlati = tab === "prodejka";
  const variantaFiltr = variantaPlati ? (varianta === "all" ? "full" : varianta) : "all";

  useEffect(() => {
    if (variantaPlati && varianta === "all") setVarianta("full");
  }, [variantaPlati, varianta]);

  const endpoint = useMemo(() => {
    const r = rozsahZFiltru(filtr);
    const p = new URLSearchParams();
    if (r) {
      p.set("od", r.od);
      p.set("do", r.do);
    } else {
      p.set("days", String(filtr.dny));
    }
    if (filtr.srovnat) p.set("srovnat", "1");
    if (variantaFiltr !== "all") p.set("varianta", variantaFiltr);
    return `/api/web-analytics?${p.toString()}`;
  }, [filtr, variantaFiltr]);

  const { data, loading, error, refetch, refreshing } = useApiData(endpoint);

  // Kolik dní vybrané období pokrývá, kvůli dennímu grafu.
  const days = useMemo(() => {
    if (!data?.obdobi) return filtr.dny;
    const d = Math.round((new Date(data.obdobi.do) - new Date(data.obdobi.od)) / 86400000);
    return Math.max(1, Math.min(400, d || filtr.dny));
  }, [data, filtr.dny]);

  const vynulovat = async () => {
    setMaze(true);
    setHlaska(null);
    try {
      const r = await apiFetch("/api/web-analytics-reset", {
        method: "POST",
        body: JSON.stringify({ vse: true }),
      });
      const j = await r.json();
      if (j.ok) {
        setHlaska({ typ: "ok", text: `Smazáno ${j.smazano} naměřených událostí. Měření běží dál.` });
        refetch();
      } else {
        setHlaska({ typ: "chyba", text: j.error || "Vynulování se nepovedlo." });
      }
    } catch (e) {
      setHlaska({ typ: "chyba", text: e.message });
    } finally {
      setMaze(false);
      setMazeDialog(false);
    }
  };

  const s = data?.souhrn || {};
  const ch = data?.chovani || {};
  const f = data?.funnel || {};
  const cs = data?.casSouhrn || {};

  // Denní řada, chybějící dny dorovnáme nulami, jinak recharts nakreslí díry.
  const denne = useMemo(() => {
    const src = data?.denne || [];
    if (!src.length) return [];
    const mapa = new Map(src.map((r) => [r.den, r]));
    const out = [];
    const konec = new Date();
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(konec);
      d.setDate(d.getDate() - i);
      const key = d.toISOString().slice(0, 10);
      const r = mapa.get(key) || {};
      out.push({
        den: key,
        label: `${d.getDate()}.${d.getMonth() + 1}.`,
        navstevnice: r.navstevnice || 0,
        zobrazeni: r.zobrazeni || 0,
        nakupy: r.nakupy || 0,
      });
    }
    return out;
  }, [data, days]);

  // Kam až došly. Sekce necháváme v pořadí, v jakém jsou na stránce.
  const trychtyrSekci = (poradi, sekce) => {
    if (!poradi?.length) return [];
    const mapa = new Map((sekce || []).map((r) => [r.section, r]));
    const celkem = (sekce || [])[0]?.celkem || 0;
    return poradi.map((p) => {
      const r = mapa.get(p.key) || {};
      return {
        key: p.key,
        label: p.label,
        videlo: r.videlo || 0,
        podil: pct(r.videlo || 0, celkem),
      };
    });
  };

  const sekceProdejka = useMemo(
    () => trychtyrSekci(data?.poradiProdejka, data?.sekceProdejka),
    [data]
  );
  const sekceNakup = useMemo(
    () => trychtyrSekci(data?.poradiNakup, data?.sekceNakup),
    [data]
  );

  // Kde největší propad mezi dvěma sousedními sekcemi. To je místo k opravě.
  const nejvetsiPropad = useMemo(() => {
    let best = null;
    for (let i = 1; i < sekceProdejka.length; i++) {
      const a = sekceProdejka[i - 1];
      const b = sekceProdejka[i];
      if (!a.videlo) continue;
      const ztrata = a.podil - b.podil;
      if (!best || ztrata > best.ztrata) best = { z: a.label, na: b.label, ztrata: Math.round(ztrata * 10) / 10 };
    }
    return best && best.ztrata > 0 ? best : null;
  }, [sekceProdejka]);

  const funnelData = useMemo(() => {
    const kroky = [
      { label: "Kvíz", value: f.kviz || 0 },
      { label: "Odeslala e-mail", value: f.odeslano || 0, popis: "Došla na /kviz/dekuji/, formulář opravdu odeslala." },
      { label: "Potvrdila e-mail", value: f.potvrzeno || 0, popis: "Klikla v e-mailu na potvrzení. Teprve tady vzniká kontakt v seznamu, kterému odejde celá série." },
      { label: "Prodejní stránka", value: f.prodejka || 0 },
      { label: "Nákupní stránka", value: f.nakup || 0 },
      { label: "Zaplaceno", value: f.dokonceno || 0 },
    ];
    const zaklad = kroky[0].value || kroky[1].value || 1;
    return kroky.map((k, i) => ({
      ...k,
      podil: pct(k.value, zaklad),
      propad: i > 0 && kroky[i - 1].value > 0 ? pct(k.value, kroky[i - 1].value) : 100,
    }));
  }, [f]);

  const konverze = pct(s.nakupy || 0, s.navstevnice || 0);

  // Míra dvojitého potvrzení. Kolik žen z těch, co formulář odeslaly, opravdu
  // klikly na potvrzovací odkaz. Pod 60 % je někde problém s doručitelností
  // nebo se ženy nedostanou k e-mailu ze složky Hromadné.
  const potvrzeniMira = f.odeslano > 0 ? pct(f.potvrzeno || 0, f.odeslano) : null;

  // ------------------------------------------------------------- hodnocení
  // Stejný jazyk jako verdikt v Marketing reportu: co funguje, co drhne
  // a co udělat první. Cíle jsou ty, které se používají i v KPI kartách:
  // konverze 5 %, potvrzení e-mailu 60 %, cena za prodej do 500 Kč, ROAS 1.
  const hodnoceni = useMemo(() => {
    const utrata = Number(data?.vydaje?.celkem) || 0;
    const nakupy = s.nakupy || 0;
    const trzba = s.trzba || 0;
    const cpd = utrata > 0 && nakupy > 0 ? Math.round(utrata / nakupy) : null;
    const roas = utrata > 0 ? Math.round((trzba / utrata) * 100) / 100 : null;
    const bezUtm = (Array.isArray(data?.zdroje) ? data.zdroje : [])
      .filter((z) => /(facebook|instagram)\.com$/i.test(String(z.zdroj || "")))
      .reduce((a, z) => a + (Number(z.navstevnice) || 0), 0);

    const funguje = [];
    const drhne = [];
    const tipy = [];

    if (!s.navstevnice) return null;

    if (konverze >= 5) funguje.push(`Konverze ${konverze} % je nad cílem 5 %.`);
    else if (nakupy > 0) {
      drhne.push(`Konverze ${konverze} %, cíl je 5 %.`);
    } else {
      drhne.push("Za tohle období není ani jeden nákup.");
      tipy.push("Projděte prodejní stránku sekci po sekci v záložce Prodejní stránka a najděte, kde čtenářky mizí.");
    }

    if (potvrzeniMira != null) {
      if (potvrzeniMira >= 60) funguje.push(`E-mail potvrdilo ${potvrzeniMira} % žen, to je zdravé.`);
      else {
        drhne.push(`E-mail potvrdilo jen ${potvrzeniMira} % žen, zbytku série nikdy neodejde.`);
        tipy.push("Zkontrolujte doručitelnost potvrzovacího e-mailu a jeho předmět. Každé procento tady je kontakt navíc zadarmo.");
      }
    }

    if (nejvetsiPropad && nejvetsiPropad.ztrata >= 25) {
      drhne.push(`Mezi ${nejvetsiPropad.z} a ${nejvetsiPropad.na} mizí ${nejvetsiPropad.ztrata} % čtenářek.`);
      tipy.push(`Přepište sekci ${nejvetsiPropad.z}. Je to místo s největší ztrátou na celé prodejce.`);
    } else if (nejvetsiPropad) {
      funguje.push(`Prodejní stránka drží, největší propad je jen ${nejvetsiPropad.ztrata} %.`);
    }

    if (cpd != null) {
      if (cpd <= 500) funguje.push(`Prodej stojí ${kc(cpd)}, cíl je do 500 Kč.`);
      else {
        drhne.push(`Prodej stojí ${kc(cpd)}, cíl je do 500 Kč.`);
        tipy.push("Než přidáte rozpočet, opravte nejslabší sekci prodejky. Levnější prodej je rychleji z konverze než z ceny za proklik.");
      }
    }

    if (roas != null) {
      if (roas >= 1) funguje.push(`ROAS ${roas}×, reklama se zaplatí.`);
      else drhne.push(`ROAS ${roas}×, reklama se zatím nezaplatí.`);
    }

    if (bezUtm > 0) {
      tipy.push(`${bezUtm} návštěvnic přišlo z Facebooku bez UTM značky. Doplňte ji do reklam, jinak jsou ceny výš zbytečně přísné.`);
    }

    const barva = drhne.length >= 3 ? "red" : drhne.length ? "amber" : "green";
    const titulek =
      barva === "green" ? "Web prodává" : barva === "amber" ? "Web prodává, ale ztrácí na cestě" : "Web zatím neprodává";
    const doporuceni =
      tipy[0] || (barva === "green" ? "Nic akutního. Držte tempo a sledujte, jestli konverze neklesá." : "Začněte u prvního bodu ve sloupci Co drhne.");

    return { barva, titulek, doporuceni, funguje, drhne, tipy: tipy.slice(0, 3) };
  }, [data, s, konverze, potvrzeniMira, nejvetsiPropad]);

  return (
    <Stranka>
      <GradientHeader
        icon={BarChart3}
        title="Web podrobně"
        napoveda="Podrobné vlastní měření fitway40.cz bez cookies: sekce stránek, odchody, mapa toku, kvíz, souhlas s cookies a tržby z FAPI. Souhrn čísel je v Analytice v Přehledu."
        loading={loading}
        akce={[
          { klic: "obnovit", ikona: RefreshCw, popis: "Obnovit", onClick: refetch, disabled: refreshing },
          { klic: "export", ikona: Download, popis: "Exportovat", onClick: () => setExportOpen(true) },
        ]}
      >
        {(jeOwner || hasRole("manager")) && (
          // Obal kvůli řadě `children` v hlavičce, která na telefonu roztahuje
          // děti na celou šířku; tlačítko nabídky má zůstat 44 px vpravo.
          <div className="flex">
            <NabidkaAkci
              tmava
              className="ml-auto"
              polozky={[
                { klic: "fapi", ikona: Receipt, popis: "Tržby z FAPI", onClick: () => setFapiOpen(true) },
                ...(jeOwner
                  ? [{ klic: "vynulovat", ikona: Trash2, popis: "Vynulovat statistiky", onClick: () => setMazeDialog(true), nebezpecna: true }]
                  : []),
              ]}
            />
          </div>
        )}
      </GradientHeader>
      <ExportStatistik open={exportOpen} onClose={() => setExportOpen(false)} />
      {(jeOwner || hasRole("manager")) && <TrzbyFapi open={fapiOpen} onClose={() => setFapiOpen(false)} />}

      {hlaska && <Hlaska ton={hlaska.typ === "ok" ? "ok" : "problem"} title={hlaska.text} />}

      {data?.srovnani && <Srovnani ted={data.souhrn} pred={data.srovnani} popis={popisSrovnani(filtr)} />}

      {!data?.empty && (
        <div className="rounded-2xl border border-slate-200 bg-white p-2">
          {/* Záložky nahoře přes celou šířku, filtr pod nimi. Vedle sebe se to
              při zalomení záložek rozpadalo. */}
          <Zalozky
            stitek="Část"
            aria-label="Část analytiky"
            value={tab}
            onChange={setTab}
            items={[
              { key: "prehled", label: "Přehled" },
              { key: "zive", label: "Živě" },
              { key: "tok", label: "Mapa toku" },
              { key: "prodejka", label: "Prodejní stránka" },
              { key: "kviz", label: "Kvíz a kalkulátor" },
            ]}
          />
          {/* Živý přehled se dívá jen na posledních pár minut, filtr období
              ani varianta pro něj nedávají smysl, tak je tam schováme. */}
          {tab !== "zive" && (
            <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-slate-100 px-1 pb-1 pt-3">
              <FiltrObdobi filtr={filtr} onChange={setFiltr} varianta="svetla" />
              {variantaPlati && (
                <PrepinacVarianty hodnota={variantaFiltr} onChange={setVarianta} bezVse />
              )}
            </div>
          )}
        </div>
      )}

      {error && <ErrorNote onRetry={refetch}>Analytiku se nepodařilo načíst: {error}</ErrorNote>}

      {loading && !data && <SkeletonCards count={6} />}

      {data?.empty && (
        <EmptyState
          icon={Globe}
          title="Zatím žádná data"
          hint="Měření běží od okamžiku, kdy se na fitway40.cz nasadil skript. První čísla se objeví, jakmile na web někdo přijde. Historii zpětně dopočítat nejde."
        />
      )}

      {data && !data.empty && tab === "prehled" && (
        <>
          <HodnoceniHero h={hodnoceni} obdobi={popisObdobi(filtr)} />
          <SrovnaniVariant radky={data.varianty} slevaCas={data.slevaCas} />
          {/* ------------------------------------------------------- souhrn */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <KpiCard
              label={<>Návštěvnice<InfoTip text="Kolik různých lidí web navštívilo. Rozlišujeme je podle denně měněného otisku, takže napříč dny se stejný člověk počítá znovu." /></>}
              value={s.navstevnice || 0}
              icon={Users}
              accent="purple"
            />
            <KpiCard label="Zobrazení stránek" value={s.zobrazeni || 0} icon={Eye} accent="blue" />
            <KpiCard label="Nákupy" value={s.nakupy || 0} icon={ShoppingCart} accent="green" />
            <KpiCard
              label={<>Konverze<InfoTip text="Podíl návštěvnic, které dokončily nákup. Cíl funnelu je 5 %." /></>}
              value={konverze}
              suffix=" %"
              icon={Percent}
              accent={konverze >= 5 ? "green" : "amber"}
            />
            <KpiCard
              label={<>Čas na návštěvnici<InfoTip text="Součet času na všech stránkách, které během návštěvy otevřela." /></>}
              value={cas(cs.prumer_s || ch.prumer_sekund)}
              icon={Timer}
              accent="slate"
            />
            <KpiCard
              label={<>Průměrný scroll<InfoTip text="Jak hluboko se v průměru dostaly po stránce, než odešly." /></>}
              value={ch.prumer_scroll || 0}
              suffix=" %"
              icon={MousePointerClick}
              accent="slate"
            />
          </div>

          {/* -------------------------------------------------- denní vývoj */}
          <Card>
            <div className="flex items-center gap-2 mb-3">
              <TrendingDown className="w-5 h-5 text-brand-600 rotate-180" />
              <h2 className="text-lg font-semibold text-slate-900">Návštěvnost po dnech</h2>
              <InfoTip text="Plocha = návštěvnice, čára = zobrazení stránek. Sloupečky dole jsou nákupy." />
            </div>
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={denne} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip
                  formatter={(v, name) => [
                    v,
                    name === "navstevnice" ? "Návštěvnice" : name === "zobrazeni" ? "Zobrazení" : "Nákupy",
                  ]}
                  labelStyle={{ fontWeight: 600 }}
                />
                <Area type="monotone" dataKey="navstevnice" stroke={BRAND} fill={BRAND} fillOpacity={0.2} strokeWidth={2} />
                <Area type="monotone" dataKey="zobrazeni" stroke={ZELENA} fill={ZELENA} fillOpacity={0.08} strokeWidth={1.5} />
                <Area type="monotone" dataKey="nakupy" stroke={RUZOVA} fill={RUZOVA} fillOpacity={0.35} strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </Card>

          {/* ------------------------------------------------------- funnel */}
          <Card>
            <div className="flex items-center gap-2 mb-4">
              <ShoppingCart className="w-5 h-5 text-brand-600" />
              <h2 className="text-lg font-semibold text-slate-900">Cesta k nákupu</h2>
              <InfoTip text="Kolik různých žen se dostalo do kterého kroku. Procento vpravo říká, kolik jich prošlo z předchozího kroku dál. Nejcitlivější místo je potvrzení e-mailu, tam se ztrácí nejvíc kontaktů, aniž by to bylo vidět." />
            </div>
            {potvrzeniMira != null && (
              <div
                className={`mb-3 flex flex-wrap items-center gap-2 rounded-xl border px-3 py-2 text-sm ${
                  potvrzeniMira >= 60
                    ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                    : "border-amber-200 bg-amber-50 text-amber-800"
                }`}
              >
                <span
                  className={`rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums ${
                    potvrzeniMira >= 60 ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-900"
                  }`}
                >
                  {potvrzeniMira} %
                </span>
                <span>
                  žen potvrdilo e-mail ({f.potvrzeno || 0} z {f.odeslano || 0}).
                  {potvrzeniMira < 60 && " Zbytku série nikdy neodejde."}
                </span>
              </div>
            )}
            <div className="space-y-2.5">
              {funnelData.map((k, i) => (
                <div key={k.label}>
                  <div className="flex items-baseline justify-between gap-3 text-sm mb-1">
                    <span className="font-medium text-slate-800">
                      {k.label}
                      {k.popis && <InfoTip text={k.popis} />}
                    </span>
                    <span className="flex shrink-0 items-center gap-2">
                      <span className="tabular-nums font-semibold text-slate-900">{k.value}</span>
                      {i > 0 && (
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums ${
                            k.propad > 100
                              ? "bg-slate-100 text-slate-500"
                              : k.propad < 30
                              ? "bg-rose-50 text-rose-700"
                              : k.propad < 60
                              ? "bg-amber-50 text-amber-700"
                              : "bg-emerald-50 text-emerald-700"
                          }`}
                          title="Kolik procent prošlo z předchozího kroku"
                        >
                          {k.propad} %
                        </span>
                      )}
                    </span>
                  </div>
                  {/* Pruh zustava cisty, podil z kvizu je pilulkou pod nim.
                      Uvnitr pruhu se text na svetlem podkladu nedal precist. */}
                  <div className="h-3 w-full overflow-hidden rounded-full bg-slate-100">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{ width: `${Math.max(k.podil, k.value > 0 ? 2 : 0)}%`, backgroundColor: BRAND, opacity: 1 - i * 0.12 }}
                    />
                  </div>
                  <div className="mt-1">
                    <span
                      className="inline-block rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold tabular-nums text-slate-600"
                      title="Podíl ze všech, které kvíz otevřely"
                    >
                      {k.podil} % z kvízu
                    </span>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-3 text-xs leading-relaxed text-slate-500">
              První tři kroky vedou ke kontaktu, další tři k prodeji. Šedé procento nad 100 % není chyba:
              na prodejní stránku chodí i ženy z e-mailů, které kvíz v tomhle období nevyplňovaly.
            </p>
          </Card>

          {/* --------------------------------------- kam až došly na prodejce */}
          <Card>
            <div className="flex items-center gap-2 mb-1">
              <TrendingDown className="w-5 h-5 text-brand-600" />
              <h2 className="text-lg font-semibold text-slate-900">Kam až došly na prodejní stránce</h2>
              <InfoTip text="Podíl žen, které danou sekci opravdu viděly. Sekce jsou v pořadí, jak jdou po sobě. Kde sloupec prudce spadne, tam je čtení přestalo bavit." />
              <span className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
                <Filter className="w-3 h-3" /> obě varianty dohromady
              </span>
            </div>
            {nejvetsiPropad && (
              <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold tabular-nums text-amber-900">
                  {nejvetsiPropad.ztrata} %
                </span>
                <span>
                  čtenářek mizí mezi <strong>{nejvetsiPropad.z}</strong> a <strong>{nejvetsiPropad.na}</strong>.
                  Tuhle část přepište první.
                </span>
              </div>
            )}
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={sekceProdejka} margin={{ top: 10, right: 12, left: 0, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} angle={-40} textAnchor="end" interval={0} height={70} />
                <YAxis tick={{ fontSize: 11 }} unit=" %" domain={[0, 100]} />
                <Tooltip formatter={(v, n, p) => [`${v} % (${p.payload.videlo} žen)`, "Vidělo"]} labelStyle={{ fontWeight: 600 }} />
                <Bar dataKey="podil" radius={[3, 3, 0, 0]}>
                  {sekceProdejka.map((e, i) => (
                    <Cell key={i} fill={e.podil >= 50 ? BRAND : e.podil >= 25 ? JANTAR : "#cbb9e0"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>

          {/* ------------------------------------- kam až došly na nákupní stránce */}
          {sekceNakup.some((x) => x.videlo > 0) && (
            <Card>
              <div className="flex items-center gap-2 mb-3">
                <TrendingDown className="w-5 h-5 text-brand-600" />
                <h2 className="text-lg font-semibold text-slate-900">Kam až došly na nákupní stránce</h2>
                <InfoTip text="Totéž pro stránku s objednávkovým formulářem. Sekce „Formulář“ je ta, kde se rozhoduje." />
              </div>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={sekceNakup} margin={{ top: 10, right: 12, left: 0, bottom: 50 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" interval={0} height={60} />
                  <YAxis tick={{ fontSize: 11 }} unit=" %" domain={[0, 100]} />
                  <Tooltip formatter={(v, n, p) => [`${v} % (${p.payload.videlo} žen)`, "Vidělo"]} />
                  <Bar dataKey="podil" radius={[3, 3, 0, 0]} fill={BRAND} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          )}

          {/* ------------------------------------------------- kde odcházejí */}
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <div className="flex items-center gap-2 mb-3">
                <h2 className="text-lg font-semibold text-slate-900">Kde odešly z prodejní stránky</h2>
                <InfoTip text="Poslední sekce, kterou žena viděla, než zavřela stránku." />
              </div>
              <TabulkaOdchodu rows={data.odchodyProdejka} poradi={data.poradiProdejka} />
            </Card>
            <Card>
              <div className="flex items-center gap-2 mb-3">
                <h2 className="text-lg font-semibold text-slate-900">Kde odešly z nákupní stránky</h2>
                <InfoTip text="Odchod ze sekce „Formulář“ znamená, že se lekla samotné objednávky." />
              </div>
              <TabulkaOdchodu rows={data.odchodyNakup} poradi={data.poradiNakup} />
            </Card>
          </div>

          {/* ----------------------------------------------------------- zdroje */}
          <SkupinyZdroju radky={data.zdrojeSkupiny} />
          <TabulkaZdroju radky={data.zdroje} />

          {/* --------------------------------------- rozcestník v profilu (/bio) */}
          <Bio souhrn={data.bioSouhrn} odkazy={data.bioOdkazy} />

          {/* ------------------------------------------------- souhlas s cookies */}
          <SouhlasCookies souhlas={data.souhlasCookies} />

          {/* ------------------------------------------------- reklama a náklady */}
          <Reklama vydaje={data.vydaje} kampane={data.kampane} zdroje={data.zdroje} funnel={f} souhrn={s} okna={data.okna} />

          {/* ---------------------------------------------------------- zařízení */}
          <div className="grid gap-4">
            <Card>
              <div className="flex items-center gap-2 mb-3">
                <h2 className="text-lg font-semibold text-slate-900">Na čem web čtou</h2>
                <InfoTip text="Podle šířky okna. U cílovky 40 až 65 let je podíl mobilů zásadní pro čitelnost." />
              </div>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={data.zarizeni || []} layout="vertical" margin={{ top: 4, right: 16, left: 40, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={GRID} horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <YAxis type="category" dataKey="zarizeni" tick={{ fontSize: 12 }} width={70} />
                  <Tooltip formatter={(v) => [v, "Návštěvnice"]} />
                  <Bar dataKey="navstevnice" radius={[0, 3, 3, 0]} fill={BRAND} />
                </BarChart>
              </ResponsiveContainer>

              {!!(data.kvizTypy || []).length && (
                <div className="mt-4 border-t border-slate-100 pt-3">
                  <div className="mb-2 text-sm font-medium text-slate-700">Výsledky kvízu</div>
                  <div className="flex flex-wrap gap-2">
                    {data.kvizTypy.map((k) => (
                      <span key={k.typ} className="rounded-full bg-brand-100 px-3 py-1 text-sm text-brand-800">
                        typ {k.typ}: <strong>{k.navstevnice}</strong>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          </div>

          {/* --------------------------------------------------------- stránky */}
          <Card>
            <div className="flex items-center gap-2 mb-3">
              <h2 className="text-lg font-semibold text-slate-900">Jednotlivé stránky</h2>
              <InfoTip text="Čas a scroll se počítají jen z odchodů, kde měření stihlo doběhnout." />
            </div>
            {/* 13.9.2026 (fáze 4i): na telefonu seznam řádků. Tabulka měla
                min-w 520 px, takže se s ní na 390 px rolovalo do stran. */}
            <div className="space-y-3 sm:hidden">
              {(data.stranky || []).map((p) => (
                <div key={p.path}>
                  <a
                    href={`https://fitway40.cz${p.path}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block break-all text-sm font-semibold text-brand-deep"
                  >
                    {p.path}
                  </a>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold tabular-nums text-slate-600">{p.navstevnice} návštěvnic</span>
                    <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold tabular-nums text-slate-600">{p.zobrazeni} zobrazení</span>
                    <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold tabular-nums text-slate-600">Ø {cas(p.prumer_sekund)}</span>
                    <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold tabular-nums text-slate-600">
                      scroll {p.prumer_scroll != null ? `${p.prumer_scroll} %` : "bez dat"}
                    </span>
                  </div>
                </div>
              ))}
              {!(data.stranky || []).length && <div className="py-4 text-center text-sm text-slate-400">Zatím nic</div>}
            </div>
            <div className="hidden overflow-x-auto sm:block">
              <table className="w-full min-w-[520px] text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-slate-500">
                    <th className="py-2 font-medium">Stránka</th>
                    <th className="py-2 text-right font-medium">Návštěvnice</th>
                    <th className="py-2 text-right font-medium">Zobrazení</th>
                    <th className="py-2 text-right font-medium">Průměrný čas</th>
                    <th className="py-2 text-right font-medium">Scroll</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.stranky || []).map((p) => (
                    <tr key={p.path} className="border-b border-slate-100 last:border-0">
                      <td className="py-2 pr-2">
                        <a
                          href={`https://fitway40.cz${p.path}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-brand-600 hover:text-brand-800 break-all"
                        >
                          {p.path}
                        </a>
                      </td>
                      <td className="py-2 text-right tabular-nums text-slate-700">{p.navstevnice}</td>
                      <td className="py-2 text-right tabular-nums text-slate-700">{p.zobrazeni}</td>
                      <td className="py-2 text-right tabular-nums text-slate-700">{cas(p.prumer_sekund)}</td>
                      <td className="py-2 text-right tabular-nums text-slate-700">
                        {p.prumer_scroll != null ? `${p.prumer_scroll} %` : "bez dat"}
                      </td>
                    </tr>
                  ))}
                  {!(data.stranky || []).length && (
                    <tr><td colSpan={5} className="py-4 text-center text-slate-400">Zatím nic</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          {/* --------------------------------------------- přechody mezi stránkami */}
          {!!(data.cesty || []).length && (
            <Card>
              <div className="flex items-center gap-2 mb-3">
                <h2 className="text-lg font-semibold text-slate-900">Nejčastější přechody</h2>
                <InfoTip text="Ze které stránky kam chodí dál. Ukáže, kudy funnel opravdu teče, ne jak jsme ho zamýšleli." />
              </div>
              <ul className="space-y-2">
                {data.cesty.map((c, i) => (
                  <li key={i} className="flex flex-wrap items-center gap-2 text-sm">
                    <code className="rounded bg-slate-100 px-2 py-0.5 text-slate-700">{c.z}</code>
                    <ArrowRight aria-hidden className="w-3.5 h-3.5 shrink-0 text-slate-400" />
                    <code className="rounded bg-slate-100 px-2 py-0.5 text-slate-700">{c.na}</code>
                    <span className="ml-auto tabular-nums font-medium text-slate-900">{c.pocet}×</span>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          <p className="px-1 pb-2 text-xs leading-relaxed text-slate-500">
            Měření neukládá do prohlížeče nic, žádné cookies ani jiné úložiště, a neuchovává IP adresy.
            Návštěvnice se rozlišují podle otisku, který se každý den mění, takže z dat nejde nikoho zpětně
            identifikovat. Proto běží i bez souhlasu s cookies a vidí i ty, které lištu odmítnou.
          </p>
        </>
      )}
      {data && !data.empty && tab === "zive" && <Zive />}

      {data && !data.empty && tab === "tok" && <FlowMapa data={data} />}

      {data && !data.empty && tab === "kviz" && <DetailKviz data={data} />}

      {data && !data.empty && tab === "prodejka" && (
        <div className="space-y-5">
          <DetailProdejky
            nazev="Prodejní stránka Jídelníček spokojené ženy"
            variantaPopis={NAZVY_VARIANT[variantaFiltr]}
            poradi={data.poradiProdejka}
            sekce={data.sekceProdejka}
            odchody={data.odchodyProdejka}
          />
          <DetailProdejky
            nazev="Nákupní stránka s objednávkovým formulářem"
            variantaPopis={NAZVY_VARIANT[variantaFiltr]}
            poradi={data.poradiNakup}
            sekce={data.sekceNakup}
            odchody={data.odchodyNakup}
          />
        </div>
      )}
      <ConfirmDialog
        open={mazeDialog}
        title="Vynulovat statistiky webu?"
        description="Smaže se všechno naměřené, návštěvy, sekce, kliknutí i konverze. Data jsou pak nevratně pryč a historie začne znovu od nuly. Měření samotné běží dál, nové návštěvy se budou zaznamenávat jako dosud."
        confirmLabel={maze ? "Mažu…" : "Ano, vynulovat"}
        variant="danger"
        onConfirm={vynulovat}
        onCancel={() => setMazeDialog(false)}
      />
    </Stranka>
  );
}

// Srovnání s předchozím obdobím. Šipka nahoru není vždy dobře, u času
// na stránce může znamenat, že se lidé prokousávají textem hůř.
// Odkud ženy přicházejí a jak daleko z každého zdroje dojdou. Ne jen
// návštěva a nákup, ale celá horní část trychtýře, protože mezi „vyplnila
// kvíz" a „mám ji v seznamu" se ztrácí nejvíc lidí a jinak to není vidět.
//
// Zdroj se počítá podle první stránky, kterou žena otevřela. Proto se
// návštěvnice nikdy nezapočítá do dvou zdrojů zároveň a součty sedí.
// Reklama a náklady. Útrata se tahá z Meta API, započítají se jen kampaně
// s předponou [Brand], tedy produkty z fitway40.cz. Kampaně na koučink mají
// [LEAD] a počítají se jinde.
//
// Ceny počítáme proti NAŠIM číslům, ne proti tomu, co hlásí Meta. Meta vidí
// jen ženy, které daly souhlas s cookies. Naše měření vidí všechny, takže
// cena za kontakt vyjde poctivě, i když Meta ve svém rozhraní ukazuje míň.
const kc = (n) => `${Math.round(Number(n) || 0).toLocaleString("cs-CZ")} Kč`;

// Souhlas s cookies — kolik žen lištu přijalo a kolik odmítlo. Rozhodnutí se
// ukládá jednou na prohlížeč, takže jde o nová rozhodnutí za vybrané období.
// Důležité pro čtení výkonu reklamy: Meta pixel vidí jen ženy, které daly „ano".
function SouhlasCookies({ souhlas }) {
  const ano = Number(souhlas?.ano) || 0;
  const ne = Number(souhlas?.ne) || 0;
  const celkem = ano + ne;
  if (!celkem) return null;
  const podil = Math.round((ano / celkem) * 100);
  return (
    <Card>
      <div className="mb-3 flex items-center gap-2">
        <h2 className="text-lg font-semibold text-slate-900">Souhlas s cookies</h2>
        <InfoTip text="Kolik žen v období kliklo na cookies lištu. Rozhodnutí se ukládá jednou na prohlížeč, takže jde o nová rozhodnutí, ne o všechny návštěvnice. Meta pixel se načte jen ženám, které daly souhlas. Naše měření běží bez cookies a vidí všechny." />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <KpiCard label="Přijalo cookies" value={String(ano)} icon={Users} accent="green" />
        <KpiCard label="Odmítlo" value={String(ne)} icon={Users} accent="amber" />
        <KpiCard
          label={<>Podíl souhlasů<InfoTip text="Přijalo děleno všemi rozhodnutími v období. Tolika procentům žen se načítá Meta pixel, zbytek Meta v číslech kampaní nevidí." /></>}
          value={`${podil} %`}
          icon={Percent}
          accent={podil >= 50 ? "green" : "amber"}
        />
      </div>
    </Card>
  );
}

function Reklama({ vydaje, kampane, zdroje, funnel, souhrn, okna }) {
  const utrata = Number(vydaje?.celkem) || 0;
  const chyba = vydaje?.error;
  const radky = Array.isArray(kampane) ? kampane : [];

  // Kampaně z Metu spárované s našimi daty podle názvu kampaně v UTM.
  // Porovnává se na malá písmena bez diakritiky, ať to nerozhodí velké písmeno.
  const klic = (s) =>
    String(s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/\s+/g, " ").trim();
  const nasePodleKlice = new Map(radky.map((r) => [klic(r.kampan), r]));

  const spojene = (vydaje?.kampane || []).map((k) => {
    const nase = nasePodleKlice.get(k.klic) || {};
    return {
      nazev: k.nazev,
      utrata: k.utrata,
      navstevnice: nase.navstevnice || 0,
      kviz: nase.kviz || 0,
      dokoncilo: nase.dokoncilo || 0,
      odeslano: nase.odeslano || 0,
      potvrzeno: nase.potvrzeno || 0,
      nakupy: nase.nakupy || 0,
      trzba: nase.trzba || 0,
    };
  });

  // -------------------------------------------------- co je „z reklamy Meta"
  // Za provoz z reklamy bereme návštěvnice, které si v UTM přinesly název
  // kampaně, na kterou v Metu opravdu utrácíme, případně jakoukoliv kampaň
  // s předponou [Brand]. Nic jiného. Referrer z Facebooku sem schválně
  // nepočítáme, protože pod ním sedí i sdílené příspěvky a organika, a to by
  // cenu za lead zkreslilo směrem dolů.
  const klicePlacene = new Set((vydaje?.kampane || []).map((k) => k.klic));
  const zReklamy = radky.filter((r) => {
    const k = klic(r.kampan);
    return klicePlacene.has(k) || k.includes("[brand]");
  });
  const soucet = (pole) => zReklamy.reduce((a, r) => a + (Number(r[pole]) || 0), 0);
  const rNavstevnice = soucet("navstevnice");
  const rKviz = soucet("kviz");
  const rOdeslano = soucet("odeslano");
  const rPotvrzeno = soucet("potvrzeno");
  const rNakupy = soucet("nakupy");
  const rTrzba = soucet("trzba");

  // Kliky z Facebooku, které dorazily bez UTM značky. Do čísel z reklamy je
  // nemůžeme započítat, ale je poctivé říct, kolik jich je, aby se cena za
  // lead nečetla jako přesná, když polovina provozu chybí.
  const bezUtm = (Array.isArray(zdroje) ? zdroje : [])
    .filter((z) => /(facebook|instagram)\.com$/i.test(String(z.zdroj || "")))
    .reduce((a, z) => a + (Number(z.navstevnice) || 0), 0);

  const potvrzeno = funnel?.potvrzeno || 0;
  const odeslano = funnel?.odeslano || 0;
  const nakupy = souhrn?.nakupy || 0;
  const trzba = souhrn?.trzba || 0;

  const cena = (delitel) => (delitel > 0 && utrata > 0 ? Math.round(utrata / delitel) : null);
  const cpl = cena(potvrzeno);
  const cpd = cena(nakupy);
  const roas = utrata > 0 ? Math.round((trzba / utrata) * 100) / 100 : null;

  const rCenaOdeslani = cena(rOdeslano);
  const rCplMereno = cena(rPotvrzeno);

  // ------------------------------------------------ dopočet potvrzených z reklamy
  // Potvrzení e-mailu se u zdroje skoro vždy ztratí. Žena klikne v potvrzovacím
  // e-mailu, odkaz vede přes app.smartemailing.cz a nám to dorazí jako nová
  // návštěva odjinud. Kdybychom útratu dělili jen tím, co se u kampaně opravdu
  // změřilo, vycházel by lead několikanásobně dráž, než jaký je.
  //
  // Odeslání formuláře se naopak děje ve stejné návštěvě jako kliknutí na
  // reklamu, takže o něj atribuce nepřijde. Bereme ho jako pevný základ a
  // násobíme mírou potvrzení z celého trychtýře, která poctivá je.
  const miraPotvrzeni = funnel?.odeslano > 0 ? (funnel.potvrzeno || 0) / funnel.odeslano : null;
  const rPotvrzenoOdhad =
    rOdeslano > 0 && miraPotvrzeni != null ? Math.round(rOdeslano * miraPotvrzeni) : null;
  const rCplOdhad = rPotvrzenoOdhad ? cena(rPotvrzenoOdhad) : null;

  const rCpd = cena(rNakupy);
  const rRoas = utrata > 0 ? Math.round((rTrzba / utrata) * 100) / 100 : null;

  // Radky kampani dopocitane jednou, at je stejne pouzije tabulka i mobilni
  // karty. Kontakty se u kampane nezmeri (potvrzovaci odkaz jde pres
  // SmartEmailing), proto se dopocitavaji mirou potvrzeni z celeho trychtyre.
  const kampaneRadky = spojene.map((k) => ({
    ...k,
    cenaK: k.odeslano > 0 ? Math.round(k.utrata / k.odeslano) : null,
    kontakty: miraPotvrzeni != null && k.odeslano > 0 ? Math.round(k.odeslano * miraPotvrzeni) : null,
  }));

  if (!utrata && !chyba) return null;

  return (
    <Card>
      <div className="mb-3 flex items-center gap-2">
        <h2 className="text-lg font-semibold text-slate-900">Reklama a náklady</h2>
        <InfoTip text="Útrata je z Meta API, počítají se jen kampaně s předponou [Brand]. Ceny se dělí našimi čísly, ne těmi z Metu, protože Meta vidí jen ženy, které daly souhlas s cookies." />
      </div>

      {chyba && (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          Útratu se nepodařilo načíst z Metu: {chyba}
        </div>
      )}

      {/* -------------------------------------------------------------- celkem
          Útrata dělená VŠEMI kontakty za období, tedy i těmi z newsletteru a
          z přímých příchodů. Je to strop toho, co vás lead stojí, když se na
          firmu díváte jako na celek. Není to výkon reklamy, ta má blok níž. */}
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
        Celkem za období, všechny zdroje dohromady
      </p>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard label="Útrata za reklamu" value={kc(utrata)} icon={Wallet} accent="slate" />
        <KpiCard
          label={<>Cena za kontakt celkem<InfoTip text="Útrata za reklamu dělená VŠEMI potvrzenými kontakty za období, tedy i těmi z newsletteru a přímých příchodů. Říká, kolik vás stojí jeden kontakt, když se díváte na firmu jako celek. Výkon samotné reklamy je v bloku níž." /></>}
          value={cpl == null ? "bez dat" : kc(cpl)}
          icon={Users}
          accent={cpl != null && cpl <= 60 ? "green" : "amber"}
        />
        <KpiCard
          label={<>Cena za prodej celkem<InfoTip text="Útrata dělená všemi nákupy za období. Cíl je 300 až 500 Kč." /></>}
          value={cpd == null ? "bez dat" : kc(cpd)}
          icon={ShoppingCart}
          accent={cpd != null && cpd <= 500 ? "green" : "amber"}
        />
        <KpiCard
          label={<>ROAS celkem<InfoTip text="Všechny tržby za období dělené útratou za reklamu. Pod 1 proděláváte." /></>}
          value={roas == null ? "bez dat" : `${roas}×`}
          icon={Percent}
          accent={roas != null && roas >= 1 ? "green" : "amber"}
        />
      </div>

      {/* ------------------------------ pevná okna: posledních 14 a 30 dní.
          Nezávislá na zvoleném filtru období, ať je trend cen vidět na první
          pohled bez přepínání. Cena = útrata z Metu za okno děleno kontakty
          (potvrzené e-maily), resp. nákupy za stejné okno. */}
      {okna && (
        <div className="mt-3 space-y-2">
          {[["Posledních 14 dní", okna.d14], ["Posledních 30 dní", okna.d30]].map(([nazev, o]) => {
            const cenaKont = o && o.utrata > 0 && o.kontakty > 0 ? Math.round(o.utrata / o.kontakty) : null;
            const cenaNakup = o && o.utrata > 0 && o.nakupy > 0 ? Math.round(o.utrata / o.nakupy) : null;
            return (
              <div key={nazev} className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700">
                <span className="font-semibold text-slate-500">{nazev}</span>
                <span aria-hidden className="h-3 w-px bg-slate-300" />
                <span className="rounded-full bg-white px-2 py-0.5 text-xs font-semibold tabular-nums text-slate-900 ring-1 ring-slate-200">
                  {kc(o.utrata)}
                </span>
                <span>útrata</span>
                <span aria-hidden className="h-3 w-px bg-slate-300" />
                <span className="rounded-full bg-white px-2 py-0.5 text-xs font-semibold tabular-nums text-slate-900 ring-1 ring-slate-200">
                  {cenaKont == null ? "bez dat" : kc(cenaKont)}
                </span>
                <span>za kontakt ({o.kontakty})</span>
                <span aria-hidden className="h-3 w-px bg-slate-300" />
                <span className="rounded-full bg-white px-2 py-0.5 text-xs font-semibold tabular-nums text-slate-900 ring-1 ring-slate-200">
                  {cenaNakup == null ? "bez dat" : kc(cenaNakup)}
                </span>
                <span>za nákup ({o.nakupy})</span>
              </div>
            );
          })}
        </div>
      )}

      {/* --------------------------------------------------- jen z reklamy Meta
          Tady se dělí jen ženami, které si v UTM přinesly název placené
          kampaně. To je skutečný výkon reklamy. Když je číslo výrazně horší
          než celkové, reklama zatím nevydělává a platí ji zbytek. */}
      <p className="mb-2 mt-6 text-xs font-semibold uppercase tracking-wide text-violet-500">
        Jen z reklamy Meta
      </p>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard
          label={<>Odeslalo formulář<InfoTip text="Počet žen, které přišly z placené kampaně a odeslaly e-mail v kvízu. Změřeno přímo: odeslání se děje ve stejné návštěvě jako kliknutí na reklamu, takže se nemá kde ztratit. Nejspolehlivější číslo v celé téhle sekci." /></>}
          value={String(rOdeslano)}
          icon={MailCheck}
          accent={rOdeslano > 0 ? "green" : "slate"}
        />
        <KpiCard
          label={<>Cena za odeslání<InfoTip text="Útrata dělená odeslanými formuláři z reklamy. Tohle číslo berte jako hlavní, je změřené. Při 70% potvrzování odpovídá ceně za kontakt do 60 Kč zhruba 42 Kč za odeslání." /></>}
          value={rCenaOdeslani == null ? "bez dat" : kc(rCenaOdeslani)}
          icon={Wallet}
          accent={rCenaOdeslani != null && rCenaOdeslani <= 42 ? "green" : "amber"}
        />
        <KpiCard
          label={<>Kontakty z reklamy <span className="font-normal text-slate-400">(odhad)</span><InfoTip text="Dopočet: odeslané formuláře z reklamy × míra potvrzení z celého trychtýře. Přímo změřit to nejde, protože potvrzovací odkaz vede přes SmartEmailing a potvrzení se u zdroje ztratí." /></>}
          value={rPotvrzenoOdhad == null ? "bez dat" : `~ ${rPotvrzenoOdhad}`}
          icon={Users}
          accent={rPotvrzenoOdhad ? "green" : "slate"}
        />
        <KpiCard
          label={<>Cena za kontakt <span className="font-normal text-slate-400">(odhad)</span><InfoTip text="Útrata dělená dopočtenými kontakty z reklamy. Je to odhad, ne měření. Cíl je do 60 Kč." /></>}
          value={rCplOdhad == null ? "bez dat" : `~ ${kc(rCplOdhad)}`}
          icon={Users}
          accent={rCplOdhad != null && rCplOdhad <= 60 ? "green" : "amber"}
        />
      </div>

      <div className="mt-4 space-y-2">
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700">
          <span className="rounded-full bg-white px-2 py-0.5 text-xs font-semibold tabular-nums text-slate-900 ring-1 ring-slate-200">
            {rNavstevnice}
          </span>
          <span>návštěvnic z reklamy</span>
          <span aria-hidden className="h-3 w-px bg-slate-300" />
          <span className="rounded-full bg-white px-2 py-0.5 text-xs font-semibold tabular-nums text-slate-900 ring-1 ring-slate-200">
            {rKviz}
          </span>
          <span>otevřelo kvíz</span>
          <span aria-hidden className="h-3 w-px bg-slate-300" />
          <span className="rounded-full bg-white px-2 py-0.5 text-xs font-semibold tabular-nums text-slate-900 ring-1 ring-slate-200">
            {rOdeslano}
          </span>
          <span>odeslalo formulář</span>
          {rCpd != null && (
            <>
              <span aria-hidden className="h-3 w-px bg-slate-300" />
              <span className="rounded-full bg-white px-2 py-0.5 text-xs font-semibold tabular-nums text-slate-900 ring-1 ring-slate-200">
                {kc(rCpd)}
              </span>
              <span>za prodej, ROAS {rRoas}×</span>
            </>
          )}
        </div>

        {rCpd == null && (
          <p className="flex items-start gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm leading-relaxed text-slate-600">
            <AlertTriangle className="mt-0.5 w-4 h-4 shrink-0 text-slate-400" />
            <span>
              Cenu za prodej z reklamy tu neukazujeme: na stránku s tajnou slevou se chodí z e-mailu,
              takže se nákup od reklamy utrhne. Použijte <strong>cenu za prodej celkem</strong> nahoře.
            </span>
          </p>
        )}

        {bezUtm > 0 && (
          <p className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm leading-relaxed text-amber-800">
            <AlertTriangle className="mt-0.5 w-4 h-4 shrink-0" />
            <span>
              Dalších <strong>{bezUtm}</strong> návštěvnic přišlo z Facebooku nebo Instagramu
              bez UTM značky. Nepočítají se sem, takže ceny výš jsou spíš vyšší než skutečné.
            </span>
          </p>
        )}
      </div>

      {!!kampaneRadky.length && (
        <div className="mt-5 border-t border-slate-100 pt-4">
          {/* Mobil: karta na kampan. Osmisloupcova tabulka se na telefonu
              nedala cist ani po posunu do strany (18.8.2026). */}
          <div className="space-y-2 sm:hidden">
            {kampaneRadky.map((k) => (
              <div key={k.nazev} className="rounded-xl border border-slate-200 bg-slate-50/60 p-3">
                <div className="font-medium text-slate-900">{k.nazev}</div>
                <div className="mt-2 flex flex-wrap gap-1.5 text-xs">
                  <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-slate-600">
                    útrata <strong className="tabular-nums text-slate-900">{kc(k.utrata)}</strong>
                  </span>
                  <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-slate-600">
                    návštěvnice <strong className="tabular-nums text-slate-900">{k.navstevnice}</strong>
                  </span>
                  <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-slate-600">
                    kvíz <strong className="tabular-nums text-slate-900">{k.kviz}</strong>
                  </span>
                  <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-slate-600">
                    odeslalo <strong className="tabular-nums text-slate-900">{k.odeslano}</strong>
                  </span>
                  <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-slate-600">
                    kontakty <strong className="tabular-nums text-slate-900">{k.kontakty == null ? "bez dat" : `~ ${k.kontakty}`}</strong>
                  </span>
                  <span
                    className={`rounded-full px-2.5 py-1 font-medium ${
                      k.cenaK == null
                        ? "border border-slate-200 bg-white text-slate-400"
                        : k.cenaK <= 42
                        ? "bg-emerald-50 text-emerald-700"
                        : "bg-amber-50 text-amber-700"
                    }`}
                  >
                    za odeslání <strong className="tabular-nums">{k.cenaK == null ? "bez dat" : kc(k.cenaK)}</strong>
                  </span>
                  <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-slate-600">
                    nákupy <strong className="tabular-nums text-slate-900">{k.nakupy}</strong>
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="hidden overflow-x-auto sm:block">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-slate-500">
                <th className="py-2 pr-2 font-medium">Kampaň</th>
                <th className="py-2 text-right font-medium">Útrata</th>
                <th className="py-2 text-right font-medium">Návštěvnice</th>
                <th className="py-2 text-right font-medium">Kvíz</th>
                <th className="py-2 text-right font-medium">Odeslalo</th>
                <th className="py-2 text-right font-medium">Kontakty</th>
                <th className="py-2 text-right font-medium">Cena za odeslání</th>
                <th className="py-2 text-right font-medium">Nákupy</th>
              </tr>
            </thead>
            <tbody>
              {kampaneRadky.map((k) => {
                const cenaK = k.cenaK;
                const kontakty = k.kontakty;
                return (
                  <tr key={k.nazev} className="border-b border-slate-100 last:border-0">
                    <td className="py-2 pr-2 font-medium text-slate-800">{k.nazev}</td>
                    <td className="py-2 text-right tabular-nums text-slate-700">{kc(k.utrata)}</td>
                    <td className="py-2 text-right tabular-nums text-slate-700">{k.navstevnice}</td>
                    <td className="py-2 text-right tabular-nums text-slate-700">{k.kviz}</td>
                    <td className="py-2 text-right tabular-nums text-slate-700">{k.odeslano}</td>
                    <td className="py-2 text-right tabular-nums font-semibold text-slate-900">
                      {kontakty == null ? "bez dat" : `~ ${kontakty}`}
                    </td>
                    <td
                      className={`py-2 text-right tabular-nums font-medium ${
                        cenaK == null ? "text-slate-400" : cenaK <= 42 ? "text-emerald-600" : "text-amber-600"
                      }`}
                    >
                      {cenaK == null ? "bez dat" : kc(cenaK)}
                    </td>
                    <td className="py-2 text-right tabular-nums text-slate-700">{k.nakupy}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>

          <p className="mt-3 text-xs leading-relaxed text-slate-500">
            Kampaně se párují podle názvu z UTM značky. Útrata bez návštěvnic znamená, že v reklamách
            chybí parametr `utm_campaign={"{{campaign.name}}"}`. Dělíme odeslanými formuláři, ne
            potvrzeními: potvrzení chodí přes SmartEmailing a u kampaně se neudrží.
          </p>
        </div>
      )}
    </Card>
  );
}

// Sloupce tabulky zdrojů. 18.8.2026 z ní zmizely „Potvrdila" a „Potvrzení":
// potvrzovací odkaz vede přes SmartEmailing, takže se návštěva utrhne od
// původního zdroje a čísla u zdroje nikdy neseděla. Poctivé celkové číslo
// je v trychtýři Cesta k nákupu nahoře.
const SLOUPCE_ZDROJU = [
  { k: "navstevnice", label: "Návštěvnice" },
  { k: "kviz", label: "Kvíz" },
  { k: "dokoncilo", label: "Dokončila", tip: "Odpověděla na všechny otázky a došla až k zadání e-mailu." },
  { k: "odeslano", label: "Odeslala", tip: "Došla na /kviz/dekuji/, tedy formulář opravdu odeslala." },
  { k: "nakupy", label: "Nákupy" },
];

// Rozcestník fitway40.cz/bio, na který vede odkaz v profilu na Instagramu.
// Nahradil Linktree, takže tady poprvé vidíme, co si lidé z Instagramu vyberou.
// Zajímavá je hlavně míra prokliku: kdo přijde a neklikne, odešel s ničím.
// Český tvar podle počtu: 1 člověk, 2 lidé, 5 lidí. Bez toho tabulka hlásila
// „1 lidí", což vypadá jako chyba v datech, i když jsou data správně.
function sklonuj(n, jeden, dva, pet) {
  if (n === 1) return jeden;
  if (n >= 2 && n <= 4) return dva;
  return pet;
}

function Bio({ souhrn, odkazy }) {
  const s = souhrn || {};
  const r = Array.isArray(odkazy) ? odkazy : [];
  const lidi = Number(s.lidi) || 0;
  const klikajici = Number(s.klikajici) || 0;
  const mira = lidi > 0 ? Math.round((klikajici / lidi) * 100) : null;
  const max = r.reduce((a, x) => Math.max(a, Number(x.lidi) || 0), 0);

  const klikyCelkem = Number(s.kliky) || 0;

  const dlazdice = [
    { label: "Přišlo lidí", hodnota: lidi, tip: "Kolik různých lidí stránku otevřelo." },
    {
      label: "Kliklo dál",
      hodnota: klikajici,
      pod: klikyCelkem > klikajici ? `${klikyCelkem} prokliků celkem` : null,
      tip: "Kolik z nich si vybralo aspoň jeden odkaz. Pod číslem je celkový počet prokliků, který je vyšší, když někdo klikl na víc odkazů.",
    },
    {
      label: "Míra prokliku",
      hodnota: mira == null ? "–" : mira + " %",
      tip: "Podíl těch, kteří klikli. Pod 40 % znamená, že odkazy nelákají nebo je jich moc.",
    },
  ];

  return (
    <Card>
      <div className="mb-3 flex items-center gap-2">
        <h2 className="text-lg font-semibold text-slate-900">Rozcestník v profilu</h2>
        <InfoTip text="Stránka fitway40.cz/bio, na kterou vede odkaz v profilu na Instagramu. Měří se přímo v ní, takže víme, který odkaz lidi zajímá." />
      </div>

      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        {dlazdice.map((d) => (
          <div key={d.label} className="rounded-xl border border-slate-200 bg-slate-50/60 p-3">
            <div className="flex items-center gap-1 text-xs text-slate-500">
              {d.label}
              <InfoTip text={d.tip} />
            </div>
            <div className="mt-1 text-xl font-semibold tabular-nums text-slate-900">{d.hodnota}</div>
            {d.pod && <div className="text-xs tabular-nums text-slate-400">{d.pod}</div>}
          </div>
        ))}
      </div>

      <div className="mt-4 space-y-2">
        {r.map((o) => {
          const l = Number(o.lidi) || 0;
          const k = Number(o.kliky) || 0;
          const podil = lidi > 0 ? Math.round((l / lidi) * 100) : 0;
          const sirka = max > 0 ? Math.max(4, Math.round((l / max) * 100)) : 0;
          return (
            <div key={o.klic} className="rounded-xl border border-slate-200 p-3">
              <div className="flex items-baseline justify-between gap-3">
                <span className="min-w-0 truncate font-medium text-slate-800">{o.popis || o.klic}</span>
                <span className="shrink-0 text-sm tabular-nums text-slate-500">
                  <strong className="text-slate-900">{l}</strong> {sklonuj(l, "člověk", "lidé", "lidí")}
                  {k > l && <>, {k} {sklonuj(k, "proklik", "prokliky", "prokliků")}</>}
                  {", "}{podil} %
                </span>
              </div>
              <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
                <div className="h-full rounded-full bg-indigo-500" style={{ width: sirka + "%" }} />
              </div>
            </div>
          );
        })}
        {!r.length && (
          <div className="py-4 text-center text-sm text-slate-400">
            Zatím nikdo neklikl. Zkontrolujte, že odkaz v profilu na Instagramu vede na fitway40.cz/bio.
          </div>
        )}
      </div>

      <p className="mt-3 text-xs leading-relaxed text-slate-500">
        Procento je podíl ze všech, kdo stránku otevřeli. Součet může přesáhnout sto procent,
        protože jeden člověk může kliknout na víc odkazů. Počet prokliků se ukazuje jen tam,
        kde je vyšší než počet lidí, tedy když někdo klikl opakovaně. V seznamu jsou pouze
        odkazy, na které někdo klikl: co tu chybí, na to zatím neklikl nikdo.
      </p>
    </Card>
  );
}

// Skupiny zdrojů. Surový seznam níž se rozpadá na l.facebook.com,
// m.facebook.com, fb, ig a podobně, takže z něj nešlo přečíst, kolik dělá
// Meta a kolik organik. Tady je totéž sloučené do hrstky skupin a placené
// je vizuálně oddělené od neplaceného. Sloučení řeší _lib/zdrojNavstevy.cjs.
function SkupinyZdroju({ radky }) {
  const r = Array.isArray(radky) ? radky : [];
  if (!r.length) return null;
  const celkem = r.reduce((a, x) => a + (Number(x.navstevnice) || 0), 0) || 1;

  return (
    <Card>
      <div className="mb-3 flex items-center gap-2">
        <h2 className="text-lg font-semibold text-slate-900">Odkud přicházejí, sloučeně</h2>
        <InfoTip text="Stejná data jako v tabulce níž, jen sloučená do skupin. Meta se jinak rozpadá na l.facebook.com, m.facebook.com, fb a ig, takže se nedá říct, kolik dělá reklama a kolik organik. Placené se pozná podle utm_medium nebo podle reklamního kliku (fbclid, gclid), který přežije i v aplikačním prohlížeči Facebooku." />
      </div>

      <div className="space-y-2">
        {r.map((z) => {
          const podil = Math.round(((Number(z.navstevnice) || 0) / celkem) * 100);
          return (
            <div key={z.klic} className="rounded-xl border border-slate-200 bg-slate-50/60 p-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-slate-900">{z.skupina}</span>
                {z.placene && (
                  <span className="rounded-full border border-amber-200 bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">placené</span>
                )}
                <span aria-hidden className="inline-block h-3 w-px bg-slate-300" />
                <span className="text-sm text-slate-600">{podil} % návštěvnic</span>
                <span className="ml-auto flex flex-wrap gap-1.5 text-xs">
                  <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-slate-600">
                    návštěvnice <strong className="tabular-nums text-slate-900">{z.navstevnice}</strong>
                  </span>
                  <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-slate-600">
                    kvíz <strong className="tabular-nums text-slate-900">{z.kviz}</strong>
                  </span>
                  <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-slate-600">
                    kontakty <strong className="tabular-nums text-slate-900">{z.kontakty}</strong>
                  </span>
                  <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-slate-600">
                    nákupy <strong className="tabular-nums text-slate-900">{z.nakupy}</strong>
                  </span>
                </span>
              </div>
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
                <div className={`h-full rounded-full ${z.placene ? "bg-amber-500" : "bg-brand-500"}`} style={{ width: `${podil}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function TabulkaZdroju({ radky }) {
  const r = Array.isArray(radky) ? radky : [];
  const soucet = (k) => r.reduce((a, x) => a + (Number(x[k]) || 0), 0);

  return (
    <Card>
      <div className="mb-3 flex items-center gap-2">
        <h2 className="text-lg font-semibold text-slate-900">Odkud přicházejí a kam dojdou</h2>
        <InfoTip text="Zdroj podle UTM značky v odkazu, jinak podle webu, ze kterého přišly. Přiřazuje se podle první stránky návštěvy, takže jedna žena patří vždy jen do jednoho zdroje." />
      </div>

      {/* Mobil: karta na zdroj. Tabulka se na telefonu nedala číst. */}
      <div className="space-y-2 sm:hidden">
        {r.map((z) => (
          <div key={z.zdroj} className="rounded-xl border border-slate-200 bg-slate-50/60 p-3">
            <div className="font-medium text-slate-900">{z.zdroj}</div>
            <div className="mt-2 flex flex-wrap gap-1.5 text-xs">
              {SLOUPCE_ZDROJU.map((c) => (
                <span key={c.k} className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-slate-600">
                  {c.label} <strong className="tabular-nums text-slate-900">{z[c.k] ?? 0}</strong>
                </span>
              ))}
            </div>
          </div>
        ))}
        {!r.length && <div className="py-4 text-center text-sm text-slate-400">Zatím nic</div>}
      </div>

      <div className="hidden overflow-x-auto sm:block">
        <table className="w-full min-w-[520px] text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-slate-500">
              <th className="py-2 pr-2 font-medium">Zdroj</th>
              {SLOUPCE_ZDROJU.map((c) => (
                <th key={c.k} className="py-2 pl-3 text-right font-medium">
                  {c.label}
                  {c.tip && <InfoTip text={c.tip} />}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {r.map((z) => (
              <tr key={z.zdroj} className="border-b border-slate-100 last:border-0">
                <td className="py-2 pr-2 font-medium text-slate-800">{z.zdroj}</td>
                {SLOUPCE_ZDROJU.map((c) => (
                  <td key={c.k} className="py-2 pl-3 text-right tabular-nums text-slate-700">
                    {z[c.k] ?? 0}
                  </td>
                ))}
              </tr>
            ))}
            {!r.length && (
              <tr>
                <td colSpan={SLOUPCE_ZDROJU.length + 1} className="py-4 text-center text-slate-400">Zatím nic</td>
              </tr>
            )}
          </tbody>
          {r.length > 1 && (
            <tfoot>
              <tr className="border-t border-slate-200 text-slate-900">
                <td className="py-2 pr-2 font-semibold">Celkem</td>
                {SLOUPCE_ZDROJU.map((c) => (
                  <td key={c.k} className="py-2 pl-3 text-right font-semibold tabular-nums">
                    {soucet(c.k)}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <p className="mt-3 text-xs leading-relaxed text-slate-500">
        Potvrzení e-mailu tu schválně není: odkaz vede přes SmartEmailing, takže se návštěva
        utrhne od zdroje a číslo by u něj lhalo. Celková míra potvrzení je v trychtýři nahoře.
      </p>
    </Card>
  );
}

// Hodnocení celého webu jedním pohledem. Stejný typ prvku jako verdikt
// v Marketing reportu, protože se čte úplně stejně: barva řekne, jak na tom
// jsme, a pod ní je co funguje, co drhne a co udělat první.
function HodnoceniHero({ h, obdobi }) {
  if (!h) return null;

  const barvy = {
    red: "from-rose-500 to-red-600",
    amber: "from-amber-500 to-orange-500",
    green: "from-emerald-500 to-teal-600",
  };
  const Ikona = h.barva === "red" ? AlertTriangle : h.barva === "green" ? CheckCircle2 : Clock;

  return (
    <div className={`overflow-hidden rounded-2xl bg-gradient-to-br ${barvy[h.barva] || barvy.amber} shadow-xl`}>
      <div className="p-5 sm:p-7">
        <div className="mb-5 flex items-start gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white/20 backdrop-blur-sm sm:h-16 sm:w-16">
            <Ikona className="w-7 h-7 text-white sm:w-8 sm:h-8" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="mb-1 text-xs font-bold uppercase tracking-widest text-white/80">Hodnocení</div>
            <div className="text-2xl font-black leading-tight text-white sm:text-3xl">{h.titulek}</div>
            <div className="mt-2 text-sm leading-relaxed text-white/95">{h.doporuceni}</div>
          </div>
        </div>

        {(h.funguje.length > 0 || h.drhne.length > 0) && (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {h.funguje.length > 0 && (
              <div className="rounded-xl border border-white/20 bg-white/10 p-4 backdrop-blur-sm">
                <div className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-white/90">
                  <TrendingUp className="w-3.5 h-3.5" /> Co funguje
                </div>
                <ul className="space-y-1.5">
                  {h.funguje.map((t, i) => (
                    <li key={i} className="text-sm leading-snug text-white/95">{t}</li>
                  ))}
                </ul>
              </div>
            )}
            {h.drhne.length > 0 && (
              <div className="rounded-xl border border-white/20 bg-white/10 p-4 backdrop-blur-sm">
                <div className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-white/90">
                  <TrendingDown className="w-3.5 h-3.5" /> Co drhne
                </div>
                <ul className="space-y-1.5">
                  {h.drhne.map((t, i) => (
                    <li key={i} className="text-sm leading-snug text-white/95">{t}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {h.tipy.length > 0 && (
          <div className="mt-3 rounded-xl border border-white/20 bg-white/10 p-4 backdrop-blur-sm">
            <div className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-white/90">
              <Sparkles className="w-3.5 h-3.5" /> Co udělat
            </div>
            <ul className="space-y-1.5">
              {h.tipy.map((t, i) => (
                <li key={i} className="text-sm leading-snug text-white/95">{t}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-5 text-xs text-white/80">Hodnoceno za {obdobi}</div>
      </div>
    </div>
  );
}

// Přepínač varianty prodejní cesty. Stejný vzhled jako přepínače v FiltrObdobi,
// ať to nahoře drží jednu řadu.
function PrepinacVarianty({ hodnota, onChange, bezVse }) {
  const volby = [
    ...(bezVse ? [] : [{ k: "all", label: "Vše" }]),
    { k: "full", label: "Bez slevy" },
    { k: "discount", label: "Tajná sleva" },
  ];
  return (
    <div className="flex rounded-xl border border-slate-200 bg-slate-50 p-0.5">
      {volby.map((v) => (
        <button
          key={v.k}
          type="button"
          onClick={() => onChange(v.k)}
          className={`rounded-xl px-2.5 py-1.5 text-sm font-medium transition ${
            hodnota === v.k ? "bg-brand-600 text-white shadow-sm" : "text-slate-600 hover:bg-slate-200/70"
          }`}
        >
          {v.label}
        </button>
      ))}
    </div>
  );
}

// Obě varianty vedle sebe. Ukazuje se vždy, nezávisle na přepínači, protože
// smysl téhle tabulky je právě to porovnání.
const NAZVY_VARIANT = {
  full: "Bez slevy",
  discount: "Tajná sleva",
  discount_expired: "Sleva po termínu",
};

function SrovnaniVariant({ radky, slevaCas }) {
  if (!Array.isArray(radky) || radky.length === 0) return null;

  const sloupce = [
    { k: "prodejka", label: "Zobrazení prodejky" },
    { k: "kliklo", label: "Kliknutí na CTA" },
    { k: "pokladna", label: "Otevření pokladny" },
    { k: "nakupy", label: "Nákupy" },
    { k: "konverze", label: "Konverze", suffix: " %" },
    { k: "prumerna_objednavka", label: "Průměrná objednávka", suffix: " Kč" },
  ];

  const okna = ["první den", "druhý den", "třetí den", "poslední den"];
  const casRadky = Array.isArray(slevaCas) ? slevaCas : [];

  return (
    <Card>
      <div className="mb-3 flex items-center gap-2">
        <h2 className="text-base font-semibold text-slate-900">Varianty prodejní cesty</h2>
        <InfoTip text="Plná cena proti tajné slevě z e-mailu. Variantu si návštěvnice nese s sebou z prodejky až na děkovací stránku, proto sedí i nákupy." />
      </div>

      {/* 14.9.2026 (fáze 6b): na telefonu je z tabulky karta na variantu,
          metriky pod sebou. Tabulka si vynucovala 520 bodů. */}
      <div className="space-y-2 sm:hidden">
        {radky.map((r) => (
          <div key={r.varianta} className="rounded-xl border border-slate-200 p-3">
            <div className="text-[15px] font-bold text-slate-900">{NAZVY_VARIANT[r.varianta] || r.varianta}</div>
            <div className="mt-1.5 divide-y divide-slate-100">
              {sloupce.map((c) => (
                <div key={c.k} className="flex items-baseline justify-between gap-2 py-1.5">
                  <span className="text-[13px] text-slate-600">{c.label}</span>
                  <span className="text-[15px] font-semibold tabular-nums text-slate-900">
                    {r[c.k] == null ? "bez dat" : `${r[c.k]}${c.suffix || ""}`}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="hidden overflow-x-auto sm:block">
        <table className="w-full min-w-[520px] text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
              <th className="pb-2 pr-3 font-medium">Metrika</th>
              {radky.map((r) => (
                <th key={r.varianta} className="pb-2 pl-3 text-right font-medium">
                  {NAZVY_VARIANT[r.varianta] || r.varianta}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sloupce.map((c) => (
              <tr key={c.k} className="border-b border-slate-100 last:border-0">
                <td className="py-2 pr-3 text-slate-600">{c.label}</td>
                {radky.map((r) => (
                  <td key={r.varianta} className="py-2 pl-3 text-right font-medium tabular-nums text-slate-900">
                    {r[c.k] == null ? "bez dat" : `${r[c.k]}${c.suffix || ""}`}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {casRadky.length > 0 && (
        <div className="mt-5 border-t border-slate-100 pt-4">
          <div className="mb-2 flex items-center gap-2">
            <h3 className="text-sm font-semibold text-slate-900">Kdy ženy se slevou míří k objednávce</h3>
            <InfoTip text="Podle toho, kolik hodin zbývalo do konce nabídky, když stránku otevřely. Řekne, kdy má smysl poslat připomínku. Počítáme kliknutí k objednávce, ne dokončené nákupy: nákup se hlásí až z děkovací stránky, kam si žena zbývající čas nepřinese." />
          </div>
          <div className="grid gap-2 sm:grid-cols-4">
            {okna.map((o) => {
              const r = casRadky.find((x) => x.okno === o);
              return (
                <div key={o} className="rounded-xl border border-slate-200 bg-slate-50/60 p-3">
                  <div className="text-xs font-medium text-slate-500">{o}</div>
                  <div className="mt-1 flex items-baseline gap-2">
                    <span className="text-lg font-semibold tabular-nums text-slate-900">{r?.kliklo || 0}</span>
                    <span className="text-xs text-slate-500">kliklo</span>
                  </div>
                  <div className="mt-0.5 text-xs text-slate-500">
                    z <span className="tabular-nums">{r?.navstevnice || 0}</span> návštěvnic
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Card>
  );
}

function Srovnani({ ted, pred, popis }) {
  const radky = [
    { label: "Návštěvnice", ted: ted?.navstevnice || 0, pred: pred?.navstevnice || 0 },
    { label: "Zobrazení", ted: ted?.zobrazeni || 0, pred: pred?.zobrazeni || 0 },
    { label: "Nákupy", ted: ted?.nakupy || 0, pred: pred?.nakupy || 0 },
  ];

  return (
    <Card>
      <div className="mb-3 flex items-center gap-2">
        <h2 className="text-base font-semibold text-slate-900">Srovnání</h2>
        <InfoTip text="Porovnáváme se stejně dlouhým obdobím těsně před vybraným." />
        <span aria-hidden className="h-3 w-px bg-slate-300" />
        <span className="text-sm text-slate-500">{popis}</span>
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        {radky.map((r) => {
          const rozdil = r.ted - r.pred;
          const procenta = r.pred > 0 ? Math.round((rozdil / r.pred) * 1000) / 10 : null;
          const roste = rozdil > 0;
          const stejne = rozdil === 0;
          const Ikona = stejne ? Minus : roste ? ArrowUp : ArrowDown;
          const barva = stejne ? "text-slate-500" : roste ? "text-emerald-700" : "text-rose-600";

          return (
            <div key={r.label} className="rounded-xl border border-slate-200 bg-slate-50/60 p-3">
              <div className="text-xs font-medium text-slate-500">{r.label}</div>
              <div className="mt-1 flex items-baseline gap-2">
                <span className="text-xl font-semibold tabular-nums text-slate-900">{r.ted}</span>
                <span className={`inline-flex items-center gap-0.5 text-sm font-medium tabular-nums ${barva}`}>
                  <Ikona className="w-3.5 h-3.5" />
                  {procenta == null ? (rozdil > 0 ? "nově" : "beze změny") : `${Math.abs(procenta)} %`}
                </span>
              </div>
              <div className="mt-0.5 text-xs text-slate-500">
                předtím <span className="tabular-nums">{r.pred}</span>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function TabulkaOdchodu({ rows, poradi }) {
  const labely = new Map((poradi || []).map((p) => [p.key, p.label]));
  const data = (rows || []).filter((r) => r.odchodu > 0);
  const max = data[0]?.odchodu || 1;

  if (!data.length) return <div className="py-4 text-center text-sm text-slate-400">Zatím nic</div>;

  return (
    <ul className="space-y-2.5">
      {data.slice(0, 8).map((r) => (
        <li key={r.sekce}>
          <div className="flex items-baseline justify-between gap-3 text-sm">
            <span className="text-slate-800">{labely.get(r.sekce) || r.sekce}</span>
            <span className="flex shrink-0 items-center gap-2 tabular-nums text-slate-500">
              <span>{r.odchodu}×</span>
              <span aria-hidden className="h-3 w-px bg-slate-300" />
              <span>{cas(r.prumer_sekund)}</span>
              <span aria-hidden className="h-3 w-px bg-slate-300" />
              <span>scroll {r.prumer_scroll != null ? `${r.prumer_scroll} %` : "bez dat"}</span>
            </span>
          </div>
          <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full" style={{ width: `${(r.odchodu / max) * 100}%`, backgroundColor: BRAND }} />
          </div>
        </li>
      ))}
    </ul>
  );
}
